/**
 * wayai run-eval — run a scenario set's enabled evals and show results
 *
 * Creates a session, runs it, polls until completion, prints summary. A session
 * targets exactly one scenario set. With no flag the run endpoint resolves the
 * hub's sole enabled set; on multi-set hubs, pick one with `--set` / `--eval`.
 *
 * Usage:
 *   wayai run-eval                    # run the hub's enabled set and wait for results
 *   wayai run-eval --set <name>       # run the named scenario set
 *   wayai run-eval --eval <name>      # run the set the named eval belongs to
 *   wayai run-eval --no-wait          # start session and exit (prints session ID)
 *   wayai run-eval --timeout 120      # wait max 2 minutes
 */

import { requireAuth, getAccessToken } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { printResultsTable, printFailingRuns } from '../lib/eval-format.js';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled', 'awaiting_review']);
const POLL_INTERVAL_MS = 3000;
const DEFAULT_TIMEOUT_S = 600;
// How many consecutive poll failures to ride out before giving up on the live
// view. At POLL_INTERVAL_MS this is ~15s of tolerance — enough to absorb a token
// rotation, KV propagation lag, or a network blip, while still failing fast on a
// genuinely broken connection rather than spinning silently until the deadline.
const MAX_CONSECUTIVE_POLL_ERRORS = 5;

export async function runEvalCommand(args: string[]): Promise<void> {
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  // Parse args
  let noWait = false;
  let timeoutSeconds = DEFAULT_TIMEOUT_S;
  let setName: string | undefined;
  let evalName: string | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--no-wait') {
      noWait = true;
    } else if (arg === '--timeout') {
      timeoutSeconds = parseInt(args[++i], 10);
      if (isNaN(timeoutSeconds) || timeoutSeconds <= 0) {
        console.error('--timeout must be a positive number (seconds)');
        process.exit(1);
      }
    } else if (arg === '--set') {
      setName = args[++i];
      if (!setName) {
        console.error('--set requires a scenario set name');
        process.exit(1);
      }
    } else if (arg === '--eval') {
      evalName = args[++i];
      if (!evalName) {
        console.error('--eval requires an eval name');
        process.exit(1);
      }
    }
  }

  if (setName && evalName) {
    console.error('Specify only one of --set or --eval.');
    process.exit(1);
  }

  // The poll below can run for minutes — longer than a short-lived OAuth JWT.
  // Re-resolving the token on a 401 (refreshing if needed; way_ tokens pass
  // through unchanged) lets the client self-heal a mid-run expiry rather than
  // aborting the whole command (gh #2605).
  const client = new ApiClient({
    apiUrl: config.api_url,
    accessToken,
    onUnauthorized: () => getAccessToken(config),
  });

  // 1. Verify hub has enabled evals
  const evalsResult = await client.getEvals(hubId, { enabled: 'true' });
  const enabledCount = evalsResult.data.evals.length;

  if (enabledCount === 0) {
    console.error('No enabled evals found for this hub. Enable evals in the platform UI first.');
    process.exit(1);
  }

  console.log(`Found ${enabledCount} enabled eval${enabledCount === 1 ? '' : 's'}`);

  // 2. Resolve the scenario set to bind. A session targets exactly one set; on
  // multi-set hubs `--set`/`--eval` disambiguates. Without a flag we leave it
  // unbound and the run endpoint resolves the hub's sole enabled set.
  let scenarioSetId: string | undefined;
  if (setName) {
    const setsResult = await client.listScenarioSets(hubId);
    const match = setsResult.data.scenario_sets.find((s) => s.set_name === setName);
    if (!match) {
      const available = setsResult.data.scenario_sets.map((s) => s.set_name).join(', ') || '(none)';
      console.error(`Scenario set "${setName}" not found. Available: ${available}`);
      process.exit(1);
    }
    scenarioSetId = match.scenario_set_id;
  } else if (evalName) {
    const match = evalsResult.data.evals.find((e) => e.eval_name === evalName);
    if (!match) {
      console.error(`No enabled eval named "${evalName}". Run \`wayai evals\` to list (it may be disabled).`);
      process.exit(1);
    }
    scenarioSetId = match.scenario_set_id;
  }

  // 3. Create session
  const now = new Date();
  const sessionName = `CLI run ${now.toISOString().slice(0, 16).replace('T', ' ')}`;
  const createResult = await client.createEvalSession(hubId, sessionName, scenarioSetId);
  const sessionId = createResult.data.session.eval_session_id;

  console.log(`Created session: ${sessionName} (${sessionId})`);

  // 4. Run session — surface backend resolution errors (e.g. a multi-set hub
  // with no `--set`) instead of leaking a raw ApiError stack.
  try {
    await client.runEvalSession(hubId, sessionId);
  } catch (err) {
    console.error(`\nFailed to start session: ${extractApiMessage(err)}`);
    process.exit(1);
  }
  console.log('Session started');

  // 5. If --no-wait, print session ID and exit
  if (noWait) {
    console.log(`\nSession ID: ${sessionId}`);
    console.log('Use `wayai eval-results --session ' + sessionId + '` to check results.');
    return;
  }

  // 6. Poll until terminal status
  const deadline = Date.now() + timeoutSeconds * 1000;
  let consecutivePollErrors = 0;

  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);

    // A single poll throwing must not tear down the live view — the session
    // keeps running server-side regardless. Ride out transient failures (token
    // rotation that survives the client refresh, KV propagation, network blip);
    // only give up once they persist past MAX_CONSECUTIVE_POLL_ERRORS (gh #2605).
    let details;
    try {
      details = await client.getEvalSessionDetails(hubId, sessionId);
      consecutivePollErrors = 0;
    } catch (err) {
      if (++consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        console.error(`\n\nLost connection to the eval session: ${extractApiMessage(err)}`);
        console.error(`The session may still be running. Check results: wayai eval-results --session ${sessionId}`);
        process.exit(1);
      }
      continue;
    }

    const session = details.data.session;
    const results = details.data.results;

    // Calculate progress
    const totalRuns = results.reduce((sum, r) => sum + r.total_runs, 0);
    const completedRuns = results.reduce((sum, r) => sum + r.successful_runs + r.failed_runs, 0);

    if (TERMINAL_STATUSES.has(session.session_status)) {
      console.log('');
      console.log(`Session ${session.session_status}: ${session.session_name} (${session.eval_session_id})\n`);
      printResultsTable(results, 'compact');

      // A red eval should explain itself here, not force a second
      // `eval-results --runs` (gh #2813). Pull per-run detail only when
      // something failed, so the all-green path keeps its single round trip.
      if (results.some((r) => r.failed_runs > 0)) {
        try {
          const runsResult = await client.getEvalSessionRuns(hubId, sessionId);
          printFailingRuns(runsResult.data.runs);
        } catch (err) {
          // Best-effort: the summary already printed. Don't fail the command on
          // a detail-fetch hiccup — point the user at the standalone view.
          console.error(`\nCould not load failure details (${extractApiMessage(err)}).`);
          console.error(`See: wayai eval-results --session ${sessionId} --runs`);
        }
      }
      return;
    }

    process.stdout.write(`\rRunning... ${completedRuns}/${totalRuns} completed`);
  }

  console.log(`\n\nTimeout after ${timeoutSeconds}s. Session is still running.`);
  console.log(`Check results: wayai eval-results --session ${sessionId}`);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Pull the human-facing `message` out of a JSON error body, falling back to the raw text. */
function extractApiMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.body) as { message?: string; error?: string };
      if (parsed.message) return parsed.message;
      if (parsed.error) return parsed.error;
    } catch {
      // body wasn't JSON — fall through to the raw text
    }
    return err.body || err.message;
  }
  return err instanceof Error ? err.message : String(err);
}
